/**
 * Session state for the app shell.
 *
 * Better Auth owns it end to end: its `get-session` returns the platform's caller
 * (`customSession` on the API: organisation, roles, permissions, modules), or
 * `null` when signed out.
 */
import {
   AppError,
   type AuthUser,
   callerSessionSchema,
   CORE_MESSAGES,
   emailSchema,
   ErrorFactories,
   IDENTITY_MESSAGES,
   msg
} from '@daka/core';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDebounce } from 'use-debounce';

import { setDeviceLocale, takePickedLocale } from '@/i18n/device-locale';
import { saveLocale } from '@/i18n/useLocales';
import { sso } from '@/lib/api';
import { unwrap } from '@/lib/http';
import { queryKeys } from '@/lib/queryKeys';
import { getSession, signIn, signOut, twoFactor } from '@/platform/auth/auth-client';

/** The session was live, but its membership or organisation is disabled (it has been signed out). */
export function isAccountDisabled(error: unknown): boolean {
   return error instanceof AppError && error.messageRef.key === IDENTITY_MESSAGES.accountDisabled;
}

/**
 * The signed-in user. A language picked on the sign-in page is saved to their
 * profile first (password or SSO alike); then the device follows the profile, so
 * the sign-in page after sign-out stays in the user's language.
 */
const fetchMe = async (): Promise<AuthUser | null> => {
   const { data } = await getSession();
   // `customSession` replaces Better Auth's default session shape with the platform's caller; its
   // client types still show the default, so the body is parsed with the caller's own schema.
   const caller = callerSessionSchema.nullable().parse(data);
   if (caller?.denied) {
      // A live session whose membership or organisation was disabled: end it; the guard says why on /login.
      await signOut();
      throw ErrorFactories.unauthorized(msg(caller.denied));
   }
   let user = caller?.user ?? null;
   if (!user) return null;
   const picked = takePickedLocale();
   if (picked && picked !== user.locale) {
      // A language the API no longer offers keeps the profile's.
      user = await saveLocale(picked).catch(() => user);
   }
   if (user) setDeviceLocale(user.locale);
   return user;
};

/** The signed-in user, or `null` when signed out — shared by the router's guards and the components. */
export const sessionQuery = queryOptions({
   queryKey: queryKeys.session,
   queryFn: fetchMe,
   staleTime: 60_000,
   retry: false
});

export function useSession() {
   return useQuery(sessionQuery);
}

export type LoginResult = { readonly kind: 'session'; readonly user: AuthUser } | { readonly kind: 'twoFactor' };

export function useLogin() {
   const queryClient = useQueryClient();

   return useMutation({
      mutationFn: async (input: { email: string; password: string }): Promise<LoginResult> => {
         const result = await signIn.email({ email: input.email, password: input.password });
         if (result.error) {
            // The per-account throttle (429) is shown as its own message; anything else is generic.
            if (result.error.status === 429) throw ErrorFactories.tooManyRequests(msg(IDENTITY_MESSAGES.tooManySignInAttempts));
            throw ErrorFactories.unauthorized(msg(CORE_MESSAGES.invalidCredentials));
         }
         if (result.data && 'twoFactorRedirect' in result.data && result.data.twoFactorRedirect) {
            return { kind: 'twoFactor' };
         }
         // A disabled membership throws here (already signed out), and the form shows why.
         const user = await fetchMe();
         if (!user) throw ErrorFactories.unauthorized(msg(CORE_MESSAGES.invalidCredentials));
         return { kind: 'session', user };
      },
      // Cache before the caller navigates — a stale signed-out `null` would bounce
      // straight back to /login when the shell mounts.
      onSuccess: (result) => {
         if (result.kind === 'session') queryClient.setQueryData(queryKeys.session, result.user);
      }
   });
}

function throwTwoFactorError(code: string | undefined): never {
   if (code === 'INVALID_PASSWORD') throw ErrorFactories.unauthorized(msg(IDENTITY_MESSAGES.mfaInvalidPassword));
   throw ErrorFactories.unauthorized(msg(IDENTITY_MESSAGES.mfaInvalidCode));
}

/** Complete the sign-in second step with a TOTP or backup code. */
export function useVerifyTwoFactor() {
   const queryClient = useQueryClient();

   return useMutation({
      mutationFn: async (input: { code: string; method: 'totp' | 'backup' }): Promise<AuthUser> => {
         const result =
            input.method === 'totp'
               ? await twoFactor.verifyTotp({ code: input.code })
               : await twoFactor.verifyBackupCode({ code: input.code });
         if (result.error) throwTwoFactorError(result.error.code);
         const user = await fetchMe();
         if (!user) throw ErrorFactories.unauthorized(msg(CORE_MESSAGES.invalidCredentials));
         return user;
      },
      onSuccess: (user) => {
         queryClient.setQueryData(queryKeys.session, user);
      }
   });
}

/** Sign in through the organisation's identity provider, chosen by email domain; the browser is redirected there. */
export function useSsoLogin() {
   return useMutation({
      mutationFn: async (email: string) => {
         const result = await signIn.sso({
            email,
            callbackURL: window.location.origin,
            errorCallbackURL: `${window.location.origin}/login`
         });
         if (result.error) throw ErrorFactories.notFound(msg(IDENTITY_MESSAGES.ssoNotConfigured));
      }
   });
}

const SSO_EMAIL_DEBOUNCE_MS = 300;

/**
 * While the user types an email on sign-in: once it is a valid address, ask whether
 * that domain has verified SSO — drives hiding the password field and showing the
 * org sign-in button. Response is `ssoAvailableSchema` via the Hono client.
 */
export function useSsoAvailable(email: string) {
   const [debounced] = useDebounce(email.trim(), SSO_EMAIL_DEBOUNCE_MS);
   const valid = emailSchema.safeParse(debounced).success;
   return useQuery({
      queryKey: queryKeys.ssoAvailable(debounced),
      queryFn: () => unwrap(sso.available.$get({ query: { email: debounced } })),
      enabled: valid,
      staleTime: 60_000
   });
}

/** Sign in with a registered passkey (one-step WebAuthn — no password). */
export function usePasskeyLogin() {
   const queryClient = useQueryClient();

   return useMutation({
      mutationFn: async (): Promise<AuthUser> => {
         const result = await signIn.passkey({ autoFill: false });
         if (result.error) throw ErrorFactories.unauthorized(msg(IDENTITY_MESSAGES.passkeyFailed));
         const user = await fetchMe();
         if (!user) throw ErrorFactories.unauthorized(msg(CORE_MESSAGES.invalidCredentials));
         return user;
      },
      onSuccess: (user) => {
         queryClient.setQueryData(queryKeys.session, user);
      }
   });
}

export function useLogout() {
   const queryClient = useQueryClient();

   return useMutation({
      mutationFn: async () => {
         await signOut();
      },
      onSuccess: () => {
         queryClient.clear();
      }
   });
}
